import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const Container = ({ children, className = "" }) => (
  <div className={`mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-10 ${className}`}>{children}</div>
);

export const Eyebrow = ({ children, light }) => (
  <p className={`eyebrow ${light ? "text-brand-200" : "text-brand"}`}>
    <span className={`eyebrow-rule ${light ? "bg-brand-200" : "bg-brand"}`} />
    {children}
  </p>
);

export const SectionHead = ({ eyebrow, title, lead, light, className = "" }) => (
  <Reveal className={`max-w-3xl ${className}`}>
    <Eyebrow light={light}>{eyebrow}</Eyebrow>
    <h2 className={`font-display mt-5 text-3xl sm:text-4xl lg:text-[2.6rem] font-semibold leading-[1.15] ${light ? "text-white" : "text-navy"}`}>{title}</h2>
    {lead && <p className={`mt-5 text-base md:text-lg leading-relaxed ${light ? "text-white/75" : "text-slate-600"}`}>{lead}</p>}
  </Reveal>
);

export const BtnLink = ({ to, children, variant = "primary", testId, className = "" }) => {
  const styles = {
    primary: "bg-navy text-white hover:bg-navy-800",
    brand: "bg-brand text-white hover:bg-brand-600",
    light: "bg-white text-navy hover:bg-mist",
    outline: "border border-navy/25 text-navy hover:border-navy hover:bg-navy hover:text-white",
    ghostLight: "border border-white/40 text-white hover:bg-white hover:text-navy",
  };
  return (
    <Link to={to} data-testid={testId} className={`btn group ${styles[variant]} ${className}`}>
      {children}
      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
    </Link>
  );
};

export const TextLink = ({ to, children, testId }) => (
  <Link to={to} data-testid={testId} className="group inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-brand transition-colors">
    {children}
    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
  </Link>
);

export const PageHero = ({ eyebrow, title, lead, img, testId }) => (
  <section data-testid={testId} className="relative isolate overflow-hidden bg-navy">
    {img && (
      <motion.img src={img} alt="" initial={{ scale: 1.08 }} animate={{ scale: 1 }} transition={{ duration: 2.2, ease: "easeOut" }}
        className="absolute inset-0 -z-10 h-full w-full object-cover" />
    )}
    <div className="absolute inset-0 -z-10 bg-gradient-to-r from-navy/90 via-navy/70 to-navy/25" />
    <Container className="py-24 sm:py-28 lg:py-36">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="max-w-3xl">
        <Eyebrow light>{eyebrow}</Eyebrow>
        <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] text-white">{title}</h1>
        {lead && <p className="mt-7 max-w-2xl border-l-[3px] border-[#2EE6D6] pl-5 text-base md:text-lg leading-relaxed text-white/85">{lead}</p>}
      </motion.div>
    </Container>
  </section>
);

export const CheckList = ({ items, light }) => (
  <ul className="space-y-3">
    {items.map((t) => (
      <li key={t} className={`flex gap-3 text-[15px] leading-relaxed ${light ? "text-white/80" : "text-slate-600"}`}>
        <span className="mt-[9px] h-1.5 w-1.5 shrink-0 bg-brand" />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);
